// src/components/calculators/uvalue/constructioncard/logic.js
import { compositeR } from '../../../../utils/uvalueCalculations'

export const makeLayer = () => ({
    id: crypto.randomUUID(),
    type: 'homogeneous',
    thickness_m: undefined,
    material: null,
    r_value: null,
});

export const makeCompositeLayer = () => ({
    id: crypto.randomUUID(),
    type: 'composite',
    thickness_m: undefined,
    woodWidth_mm: null,
    ccDistance_mm: null,
    lambdaWood: 0.12,
    lambdaIns: null,
    fractionOverride: null,
});

/* ---------- insulation helpers (A.2 / A.3) ---------- */
const INSULATION_CAT = /(Isover|EPS|XPS|PIR|PUR|Celleglas|Granulat|Insulsafe|Leca)/i;
const INSULATION_NAME = /(isol|isolering|glasuld|stenuld|mineraluld|rockwool|eps|xps|pir|pur|celleglas|granulat|cellulose|hamp|uld|foam|skum)/i;
const EXCLUDE_NAME = /(træbeton|hulrum|pudslag|trapez|stål|gips|osb|spånplade|krydsfiner|tegl|beton)/i;

const looksLikeInsulation = (name = '') => INSULATION_NAME.test(name) && !EXCLUDE_NAME.test(name);
const isInsulationItem = (categories, ci, ii, fallbackName = '') => {
    const cat = categories?.[ci]?.category || '';
    const name = (categories?.[ci]?.items?.[ii]?.name || fallbackName || '');
    return (INSULATION_CAT.test(cat) || looksLikeInsulation(name)) && !EXCLUDE_NAME.test(name);
};

export const insulationR = (l, categories) => {
    if (!l) return 0;

    if (l.type === 'composite') {
        const t = +l.thickness_m || 0;
        const lamIs = +l.lambdaIns || 0;
        return t && lamIs ? t / lamIs : 0;
    }

    if (l.type === 'homogeneous' && l.kind === 'lambda') {
        const t = +l.thickness_m || 0, lam = +l.lambda || 0;
        const name = (l.itemName || l.customName || '');
        const inCat = (l.catIndex != null && l.itemIndex != null)
            ? isInsulationItem(categories, l.catIndex, l.itemIndex, name)
            : looksLikeInsulation(name);
        return (t && lam && inCat) ? (t / lam) : 0;
    }

    if (l.type === 'customMaterial' && l.mode === 'lambda') {
        const t = +l.thickness_m || 0, lam = +l.customLambda || 0;
        const name = (l.customName || '');
        return (t && lam && looksLikeInsulation(name)) ? (t / lam) : 0;
    }

    if (l.type === 'homogeneous' && l.kind === 'fixedR') {
        const name = (l.itemName || l.customName || '');
        return looksLikeInsulation(name) ? (+l.rValue || 0) : 0;
    }

    if (l.type === 'customMaterial' && l.mode === 'fixedR') {
        const name = (l.customName || '');
        return looksLikeInsulation(name) ? (+l.customR || 0) : 0;
    }

    return 0;
};

export const autoR1ForA3 = (layers = [], categories = [], d1_m = null) => {
    // Build insulation candidates with R and thickness t (if known)
    const cands = [];
    for (const l of layers) {
        if (l.type === 'composite') {
            const t = +l.thickness_m || 0, lamIs = +l.lambdaIns || 0;
            if (t > 0 && lamIs > 0) cands.push({ R: t / lamIs, t });
            continue;
        }
        if (l.type === 'homogeneous' && l.kind === 'lambda') {
            const t = +l.thickness_m || 0, lam = +l.lambda || 0;
            const name = (l.itemName || l.customName || '');
            const ok = (l.catIndex != null && l.itemIndex != null)
                ? isInsulationItem(categories, l.catIndex, l.itemIndex, name)
                : looksLikeInsulation(name);
            if (ok && t > 0 && lam > 0) cands.push({ R: t / lam, t });
            continue;
        }
        if (l.type === 'customMaterial' && l.mode === 'lambda') {
            const t = +l.thickness_m || 0, lam = +l.customLambda || 0;
            const name = (l.customName || '');
            if (looksLikeInsulation(name) && t > 0 && lam > 0) cands.push({ R: t / lam, t });
            continue;
        }
        if (l.type === 'homogeneous' && l.kind === 'fixedR') {
            const name = (l.itemName || l.customName || '');
            if (looksLikeInsulation(name)) {
                const R = +l.rValue || 0, t = +l.thickness_m || null;
                if (R > 0) cands.push({ R, t });
            }
            continue;
        }
        if (l.type === 'customMaterial' && l.mode === 'fixedR') {
            const name = (l.customName || '');
            if (looksLikeInsulation(name)) {
                const R = +l.customR || 0, t = +l.thickness_m || null;
                if (R > 0) cands.push({ R, t });
            }
            continue;
        }
    }
    if (!cands.length) return 0;

    const d1 = Number(d1_m);
    // If the user specified d1, try to match a single insulation thickness within 15%
    if (Number.isFinite(d1) && d1 > 0) {
        const withT = cands.filter(c => Number.isFinite(c.t) && c.t > 0);
        if (withT.length) {
            let best = withT[0], bestErr = Math.abs(withT[0].t - d1);
            for (const c of withT.slice(1)) {
                const e = Math.abs(c.t - d1);
                if (e < bestErr) { best = c; bestErr = e; }
            }
            if (Math.abs(best.t - d1) <= 0.15 * d1) return best.R;
        }
        // If no decent thickness match, fall through to default below
    }

    // DEFAULT: assume fasteners pass through the whole insulation pack
    // Return the SUM of insulation R for all insulation layers.
    return cands.reduce((s, c) => s + (c.R || 0), 0);
};


/* ---------- setters for <Allowances> ---------- */
export const allowSetters = ({ data, patch }) => ({
    ...Object.fromEntries(
        Object.keys(data.allow || {}).map(k => [
            `set${k[0].toUpperCase()}${k.slice(1)}`,
            v => patch(prev => ({ allow: { ...prev.allow, [k]: v } })),
        ])
    ),
    setAllow: o => patch(prev => ({ allow: { ...prev.allow, ...o } })),
});

/* ---------- catalogue save/update/delete (custom + composite) ---------- */
const cloneCats = prev => (Array.isArray(prev) ? prev.map(c => ({ ...c, items: [...(c.items || [])] })) : []);
const ensureCustomCat = cats => {
    let ci = cats.findIndex(c => c?.category === 'Egne materialer');
    if (ci === -1) { ci = cats.length; cats.push({ category: 'Egne materialer', items: [] }); }
    return ci;
};
const ensureCompositeCat = cats => {
    let ci = cats.findIndex(c => c?.category === 'Inhomogene lag');
    if (ci === -1) { ci = cats.length; cats.push({ category: 'Inhomogene lag', items: [] }); }
    return ci;
};

// structural equality for de-dup of composites (LEGACY only — no sublayers)
function deepEqualComposite(a, b) {
    const pick = it => it && it.kind === 'composite'
        ? {
            kind: 'composite',
            lambdaIns: +it.lambdaIns || null,
            lambdaWood: +it.lambdaWood || null,
            woodWidth_mm: +it.woodWidth_mm || null,
            ccDistance_mm: +it.ccDistance_mm || null,
            thickness_m: +it.thickness_m || null,
        }
        : null;
    return JSON.stringify(pick(a)) === JSON.stringify(pick(b));
}

export const saveCustom = ({ latest, categories, setCategories, patch }) => {
    if (!latest || latest.type !== 'customMaterial') return;

    const isFixedR = latest.mode === 'fixedR';

    const toNumber = (v) => {
        if (v === '' || v == null) return null;
        const n = typeof v === 'string' ? Number.parseFloat(String(v).replace(',', '.')) : Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const val = toNumber(isFixedR ? latest.customR : latest.customLambda);

    const tNum = toNumber(latest.thickness_m);
    const thickness = Number.isFinite(tNum) && tNum > 0 ? tNum : null;

    const cats = cloneCats(categories);
    const ci = ensureCustomCat(cats);
    const items = cats[ci].items;

    const baseName = (latest.customName || '').trim()
        || (isFixedR ? `R = ${val.toFixed(3)}` : `λ = ${val.toFixed(3)}`);
    const taken = items.map(i => (i.name || '').trim().toLowerCase());
    let name = (baseName || 'Eget materiale').trim();
    if (taken.includes(name.toLowerCase())) {
        let k = 1; while (taken.includes(`${name} (${k})`.toLowerCase())) k += 1;
        name = `${name} (${k})`;
    }

    // Persist thickness on the ITEM only for fixed R (rule 2). For lambda, omit it.
    const itemForFixedR = { name, kind: 'fixedR', rValue: val, thickness_m: thickness };
    const itemForLambda = { name, kind: 'lambda', lambda: val, thickness_m: thickness };

    // Find existing item (ignore thickness for lambda, include it for fixed R)
    let ii;
    if (isFixedR) {
        ii = items.findIndex(it =>
            it.kind === 'fixedR' &&
            Math.abs((it.rValue ?? NaN) - val) < 1e-9 &&
            ((it.thickness_m == null && thickness == null) ||
                (Number.isFinite(it.thickness_m) && Number.isFinite(thickness) &&
                    Math.abs(it.thickness_m - thickness) < 1e-9))
        );
    } else {
        // For lambda match both λ and (if present) t; if neither side has t we match on λ alone
        ii = items.findIndex(it =>
            it.kind === 'lambda' &&
            Math.abs((it.lambda ?? NaN) - val) < 1e-9 &&
            (
                (thickness == null && it.thickness_m == null) ||
                (Number.isFinite(thickness) && Number.isFinite(it.thickness_m) &&
                    Math.abs(it.thickness_m - thickness) < 1e-9)
            )
        );
    }

    if (ii === -1) {
        items.push(isFixedR ? itemForFixedR : itemForLambda);
        ii = items.length - 1;
    } else {
        // For lambda, never inject thickness_m into an existing item.
        if (isFixedR) {
            items[ii] = { ...items[ii], ...itemForFixedR, name: items[ii].name || itemForFixedR.name };
        } else {
            items[ii] = { ...items[ii], ...itemForLambda, name: items[ii].name || itemForLambda.name };
        }
    }

    setCategories(cats);


    patch(prev => ({
        layers: prev.layers.map(l => {
            if (l.id !== latest.id) return l;

            if (isFixedR) {
                // For fixed R: store item defaults (incl. thickness), but never wipe an explicit layer thickness.

                const layerThickness = (thickness != null ? thickness : (items[ii].thickness_m ?? null));

                return {
                    id: l.id,
                    type: 'homogeneous',
                    kind: 'fixedR',
                    catIndex: ci,
                    itemIndex: ii,
                    itemName: items[ii].name,
                    rValue: val,
                    thickness_m: layerThickness,
                };
            }

             const layerThickness = (thickness != null ? thickness : (items[ii].thickness_m ?? null));


            return {
                id: l.id,
                type: 'homogeneous',
                kind: 'lambda',
                catIndex: ci,
                itemIndex: ii,
                itemName: items[ii].name,
                lambda: val,
                thickness_m: layerThickness,
            };
        })
    }));

};

export const saveComposite = ({ latest, categories, setCategories, patch }) => {
    if (!latest || latest.type !== 'composite') return;

    const num = v => {
        if (v === '' || v == null) return null;
        const n = typeof v === 'string' ? parseFloat(String(v).replace(',', '.')) : Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const λI = num(latest.lambdaIns) ?? 0.040;
    const λW = num(latest.lambdaWood) ?? 0.12;

    const woodWidth = num(latest.woodWidth_mm);
    const cc = num(latest.ccDistance_mm);
    const t = num(latest.thickness_m);
    const thickness = t != null && t > 0 ? t : null;

    if (!λI || !woodWidth || !cc || woodWidth >= cc) return;

    const baseName = (latest.customName || latest.name || '').trim()
        || `Træ ${woodWidth} mm / c.${cc} mm, λ isol ${λI?.toFixed?.(3)}`;

    const item = {
        name: baseName,
        kind: 'composite',
        lambdaIns: λI,
        lambdaWood: λW,
        woodWidth_mm: woodWidth,
        ccDistance_mm: cc,
        thickness_m: thickness,
        rValue: compositeR({
            type: 'composite',
            lambdaIns: λI, lambdaWood: λW,
            woodWidth_mm: woodWidth,
            ccDistance_mm: cc,
            thickness_m: thickness
        }),
    };

    const cats = cloneCats(categories);
    const ci = ensureCompositeCat(cats);
    const items = cats[ci].items;

    let ii = items.findIndex(it => deepEqualComposite(it, item));
    if (ii === -1) { items.push(item); ii = items.length - 1; }
    else { items[ii] = { ...items[ii], ...item, name: items[ii].name || item.name }; }

    const taken = items.map(i => (i.name || '').trim().toLowerCase());
    if (taken.filter(n => n === (item.name || '').trim().toLowerCase()).length > 1) {
        let k = 1; let proposal;
        do { proposal = `${item.name} (${k++})`; }
        while (items.some(x => (x.name || '').trim().toLowerCase() === proposal.toLowerCase()));
        items[ii].name = proposal;
    }

    setCategories(cats);

    patch(prev => ({
        layers: prev.layers.map(l => l.id === latest.id
            ? {
                ...l,
                type: 'composite',
                kind: 'composite',
                catIndex: ci,
                itemIndex: ii,
                itemName: items[ii].name,
                lambdaIns: items[ii].lambdaIns ?? null,
                lambdaWood: items[ii].lambdaWood ?? null,
                woodWidth_mm: items[ii].woodWidth_mm ?? null,
                ccDistance_mm: items[ii].ccDistance_mm ?? null,
                thickness_m : (thickness != null ? thickness : (items[ii].thickness_m ?? null)),
                sublayers: undefined,
            }
            : l)
    }));
};

export const updateCompositeItem = ({ id, patchItem, setCategories }) => {
    setCategories(prev => {
        const cats = cloneCats(prev);
        const ci = ensureCompositeCat(cats);
        const ii = Number(id);
        if (!Number.isInteger(ii) || !cats[ci]?.items?.[ii]) return prev;

        const num = v => (v === '' || v == null ? null : Number(v));

        const name = (patchItem.name || '').trim() || 'Inhomogent lag';
        const lambdaIns = num(patchItem.lambdaIns);
        const lambdaWood = num(patchItem.lambdaWood);
        const woodWidth_mm = num(patchItem.woodWidth_mm);
        const ccDistance_mm = num(patchItem.ccDistance_mm);
        const thickness_m = patchItem.thickness_m == null ? null : Number(patchItem.thickness_m);

        const item = {
            ...cats[ci].items[ii],
            name,
            kind: 'composite',
            lambdaIns,
            lambdaWood,
            woodWidth_mm,
            ccDistance_mm,
            thickness_m,
        };

        item.rValue = compositeR({
            type: 'composite',
            lambdaIns: lambdaIns ?? 0.040,
            lambdaWood: lambdaWood ?? 0.12,
            woodWidth_mm: woodWidth_mm ?? 0,
            ccDistance_mm: ccDistance_mm ?? 0,
            thickness_m: thickness_m ?? null,
        });

        cats[ci].items[ii] = item;
        return cats;
    });
};

/* ---------- CRUD for manage modal ---------- */
export const createCustomItem = ({ draft, setCategories }) => {
    setCategories(prev => {
        const cats = cloneCats(prev); const ci = ensureCustomCat(cats);
        const items = cats[ci].items;
        const item = draft.kind === 'fixedR'
            ? { name: draft.name, kind: 'fixedR', rValue: Number(draft.rValue) || 0, thickness_m: draft.thickness_m ?? null }
            : { name: draft.name, kind: 'lambda', lambda: Number(draft.lambda) || 0, thickness_m: draft.thickness_m ?? null };
        items.push(item);
        return cats;
    });
};

export const createCompositeItem = ({ draft, setCategories }) => {
    setCategories(prev => {
        const cats = cloneCats(prev);
        const ci = ensureCompositeCat(cats);
        const items = cats[ci].items;

        // helper → coerces value or falls back to default/null
        const num = (v, d = null) => (v === '' || v == null ? d : Number(v));

        const lambdaIns = num(draft.lambdaIns, 0.040);
        const lambdaWood = num(draft.lambdaWood, 0.12);
        const woodWidth_mm = num(draft.woodWidth_mm);
        const ccDistance_mm = num(draft.ccDistance_mm);
        const thickness_m = draft.thickness_m == null ? null : Number(draft.thickness_m);

        const item = {
            name: (draft.name || '').trim() || 'Inhomogent lag',
            kind: 'composite',
            lambdaIns,
            lambdaWood,
            woodWidth_mm,
            ccDistance_mm,
            thickness_m,
        };

        // pre-compute catalogue R-value
        item.rValue = compositeR({ type: 'composite', ...item });

        items.push(item);
        return cats;
    });
};


export const updateCustomItem = ({ id, patchItem, setCategories }) => {
    setCategories(prev => {
        const cats = cloneCats(prev); const ci = ensureCustomCat(cats);
        const ii = Number(id); if (!Number.isInteger(ii) || !cats[ci].items[ii]) return prev;
        const next = {
            name: patchItem.name,
            kind: patchItem.kind,
            // Only keep thickness on fixedR items; clear it for lambda items
            thickness_m: patchItem.kind === 'fixedR' ? (patchItem.thickness_m ?? null) : null,
        };
        if (patchItem.kind === 'fixedR') next.rValue = Number(patchItem.rValue) || 0;
        else next.lambda = Number(patchItem.lambda) || 0;
        cats[ci].items[ii] = next;
        return cats;
    });
};

export const deleteCustomItem = ({ id, categories, setCategories, patch }) => {
    const ii = Number(id); let removed = null;
    setCategories(prev => {
        const cats = cloneCats(prev); const ci = ensureCustomCat(cats);
        const items = cats[ci].items;
        if (!Number.isInteger(ii) || !items[ii]) return prev;
        removed = items[ii]; items.splice(ii, 1);
        return cats;
    });
    if (!removed) return;

    const ciNow = (Array.isArray(categories) ? categories : []).findIndex(c => c?.category === 'Egne materialer');
    patch(prev => ({
        layers: prev.layers.map(l => {
            if (l.catIndex !== ciNow) return l;
            if (l.itemIndex === ii) {
                return removed.kind === 'lambda'
                    ? {
                        ...l, type: 'customMaterial', mode: 'lambda', customName: removed.name, customLambda: removed.lambda,
                        thickness_m: l.thickness_m ?? removed.thickness_m ?? null, catIndex: null, itemIndex: null, kind: undefined, lambda: undefined
                    }
                    : {
                        ...l, type: 'customMaterial', mode: 'fixedR', customName: removed.name, customR: removed.rValue,
                        thickness_m: l.thickness_m ?? removed.thickness_m ?? null, catIndex: null, itemIndex: null, kind: undefined, rValue: undefined
                    };
            }
            if (l.itemIndex > ii) return { ...l, itemIndex: l.itemIndex - 1 };
            return l;
        })
    }));
};

export const deleteCompositeItem = ({ id, categories, setCategories, patch }) => {
    const ii = Number(id); let removed = null;
    setCategories(prev => {
        const cats = cloneCats(prev); const ci = ensureCompositeCat(cats);
        const items = cats[ci].items;
        if (!Number.isInteger(ii) || !items[ii]) return prev;
        removed = items[ii]; items.splice(ii, 1);
        return cats;
    });
    if (!removed) return;

    const ciNow = (Array.isArray(categories) ? categories : []).findIndex(c => c?.category === 'Inhomogene lag');
    patch(prev => ({
        layers: prev.layers.map(l => {
            if (l.catIndex !== ciNow) return l;
            if (l.itemIndex === ii) {
                return {
                    ...l,
                    type: 'composite',
                    kind: 'composite',
                    catIndex: null,
                    itemIndex: null,
                    itemName: undefined,
                    customName: removed.name,
                    lambdaIns: removed.lambdaIns ?? null,
                    lambdaWood: removed.lambdaWood ?? null,
                    woodWidth_mm: removed.woodWidth_mm ?? null,
                    ccDistance_mm: removed.ccDistance_mm ?? null,
                    thickness_m: l.thickness_m ?? removed.thickness_m ?? null,
                    sublayers: undefined,
                };
            }
            if (l.itemIndex > ii) return { ...l, itemIndex: l.itemIndex - 1 };
            return l;
        })
    }));
};

export const getCustomUsageCount = ({ id, categories, data }) => {
    const ci = (Array.isArray(categories) ? categories : []).findIndex(c => c?.category === 'Egne materialer');
    const ii = Number(id); if (ci === -1 || !Number.isInteger(ii)) return 0;
    return data.layers.filter(l => l.catIndex === ci && l.itemIndex === ii).length;
};

export const getCompositeUsageCount = ({ id, categories, data }) => {
    const ci = (Array.isArray(categories) ? categories : []).findIndex(c => c?.category === 'Inhomogene lag');
    const ii = Number(id); if (ci === -1 || !Number.isInteger(ii)) return 0;
    return data.layers.filter(l => l.catIndex === ci && l.itemIndex === ii).length;
};
